// @ts-check
// Taylor's Boats — VDP videos carousel.
// Horizontal scroll-snap track with prev/next buttons; click a card to
// open the shared `<tb-video-modal>` (same modal pattern as the homepage
// value-cards-video and os-video-cards sections).
//
// A card carries either:
//   data-video-type="file" + data-video-src="…"  → native <video> playback
//   data-video-url="…"                            → YouTube/Vimeo embed
// The number of cards is variable (per-product metaobject list or the
// section's general/default blocks), so paging is computed from the DOM.

class VdpVideos extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLElement | null} */
    this.track = this.querySelector('.vv-track');
    /** @type {HTMLElement | null} */
    this.prevBtn = this.querySelector('[data-vv-prev]');
    /** @type {HTMLElement | null} */
    this.nextBtn = this.querySelector('[data-vv-next]');
    /** @type {HTMLElement[]} */
    this.cards = Array.from(this.querySelectorAll('[data-vv-card]'));
    /** @type {(HTMLElement & { open?: (source: any, trigger: HTMLElement) => void }) | null} */
    this.modal = this.querySelector('tb-video-modal');

    if (!this.track || this.cards.length === 0) return;

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.playCard(card));
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByCard(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByCard(1));
    }

    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', () => this.updateArrows(), { passive: true });
    this.updateArrows();
  }

  /** @param {number} dir */
  scrollByCard(dir) {
    if (!this.track) return;
    const card = /** @type {HTMLElement | null} */ (this.track.querySelector('.vv-cell'));
    if (!card) return;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    const step = card.getBoundingClientRect().width + gap;
    // Advance by a full view (the number of cards currently visible) so the
    // carousel pages cleanly when there are more than the visible count.
    const perView = Math.max(1, Math.round(this.track.clientWidth / step));
    this.track.scrollBy({ left: step * perView * dir, behavior: 'smooth' });
  }

  updateArrows() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    const atStart = x <= 1;
    const atEnd = x >= max - 1;
    const isOverflowing = max > 1;

    // Hide the arrows entirely when nothing overflows (≤ one full view of
    // cards). When the carousel does overflow, keep both arrows visible and
    // mark the one at the current extreme as disabled — matches the design,
    // which always shows the pair as long as the carousel is scrollable.
    if (this.prevBtn) {
      this.prevBtn.hidden = !isOverflowing;
      this.prevBtn.disabled = atStart;
    }
    if (this.nextBtn) {
      this.nextBtn.hidden = !isOverflowing;
      this.nextBtn.disabled = atEnd;
    }
  }

  /** @param {HTMLElement} card */
  playCard(card) {
    if (!this.modal || typeof this.modal.open !== 'function') return;

    // Uploaded file → native playback descriptor; the card carries the file
    // URL (and an optional poster) so the modal can play it without an embed.
    if (card.dataset.videoType === 'file' && card.dataset.videoSrc) {
      this.modal.open(
        {
          type: 'file',
          url: card.dataset.videoSrc,
          poster: card.dataset.videoPoster || '',
        },
        card,
      );
      return;
    }

    // YouTube/Vimeo URL → embed path (unchanged string signature).
    const videoUrl = card.dataset.videoUrl;
    // No URL → nothing to open; matches the homepage video carousel behavior
    // for cards with placeholder content still being filled in.
    if (!videoUrl) return;
    this.modal.open(videoUrl, card);
  }
}

if (!customElements.get('vdp-videos')) {
  customElements.define('vdp-videos', VdpVideos);
}
